<template>
    <AppModal ref="modal" :title="$t('labels.create_waybill')" size="full">
        <div class="modal-body">
            <div class="row">
                <FormGroup class="col-lg-3" :label="$t('labels.delivery_service')">
                    <DeliveryMethodSelect :model-value="DeliveryIntegrationsEnum.ROZETKA" disabled/>
                </FormGroup>
                <FormGroup class="col-lg-3" :label="$t('labels.recipient_name')">
                    <Input :model-value="recipientName" disabled/>
                </FormGroup>
                <FormGroup class="col-lg-2" :label="$t('labels.recipient_phone')">
                    <PhoneInput :model-value="recipientPhone" disabled/>
                </FormGroup>
                <FormGroup class="col-lg-2" :label="$t('labels.city')">
                    <Input :model-value="formatDeliveryCityName(recipientCity)" disabled/>
                </FormGroup>
                <FormGroup class="col-lg-2" :label="$t('labels.department')">
                    <Input :model-value="recipientWarehouse?.name ?? ''" disabled/>
                </FormGroup>
            </div>
            <hr/>
            <div class="d-flex align-items-center mb-4">
                <h6 class="mb-0 font-weight-semibold">Створення ТТН</h6>
                <span class="badge badge-soft-warning ml-2">Перевірте дані перед створенням ТТН</span>
            </div>
            <div class="row">
                <FormGroup class="col-lg-1" :label="$t('labels.seats')">
                    <Input v-model="seatsAmount" type="number"/>
                </FormGroup>
                <FormGroup class="col-lg-1" :label="$t('labels.weight')">
                    <Input v-model="weight" type="number"/>
                </FormGroup>
                <FormGroup class="col-lg-4" :label="$t('labels.dimensions')">
                    <div class="d-flex align-items-center">
                        <Input v-model="length" type="number"/>
                        <span class="text-muted mx-2">×</span>
                        <Input v-model="width" type="number"/>
                        <span class="text-muted mx-2">×</span>
                        <Input v-model="height" type="number"/>
                    </div>
                </FormGroup>
                <FormGroup class="col-lg-6" :label="$t('labels.cargo_description')">
                    <Input v-model="description" :max-length="100"/>
                </FormGroup>
                <FormGroup class="col-lg-2" :label="$t('labels.cod')">
                    <AppButton
                        class="btn-block"
                        :color="codEnabled ? 'indigo' : 'light'"
                        @click="codEnabled = !codEnabled"
                    >
                        <i class="icon-coin-dollar mr-2"></i>
                        {{ codEnabled ? $t('labels.enabled') : $t('labels.disabled') }}
                    </AppButton>
                </FormGroup>
                <FormGroup class="col-lg-2" :label="$t('labels.declared_value')">
                    <Input :model-value="orderSum" type="number" disabled/>
                </FormGroup>
                <FormGroup class="col-lg-2" :label="$t('labels.cod_amount')" :required="codEnabled">
                    <div class="d-flex align-items-center">
                        <span class="text-muted mr-2">=</span>
                        <Input v-model="codAmount" type="number" :disabled="!codEnabled"/>
                    </div>
                </FormGroup>
                <FormGroup class="col-lg-2" :label="$t('labels.insurance')">
                    <AppButton
                        class="btn-block"
                        :color="isInsuranceEnabled ? 'indigo' : 'light'"
                        @click="isInsuranceEnabled = !isInsuranceEnabled"
                    >
                        <i class="icon-shield-check mr-2"></i>
                        {{ isInsuranceEnabled ? $t('labels.enabled') : $t('labels.disabled') }}
                    </AppButton>
                </FormGroup>
            </div>
            <AppToast :title="senderTitle" class="mt-3 mb-3">
                <template #headerElements>
                    <span class="text-muted text-truncate mr-2">{{ senderSummary }}</span>
                    <i
                        role="button"
                        :class="senderExpanded ? 'icon-arrow-up12' : 'icon-arrow-down12'"
                        @click="senderExpanded = !senderExpanded"
                    ></i>
                </template>
                <div class="toast-body pb-0" v-show="senderExpanded">
                    <div class="row">
                        <FormGroup class="col-lg-2" :label="$t('labels.type')" required>
                            <Input :model-value="senderType ? $t(`enums.RozetkaSenderTypeEnum.${senderType}`) : ''" readonly/>
                        </FormGroup>
                        <FormGroup class="col-lg-3" :label="$t('labels.full_name')" required>
                            <Input :model-value="senderName" readonly/>
                        </FormGroup>
                        <FormGroup class="col-lg-2" :label="$t('labels.phone')" required>
                            <PhoneInput :model-value="senderPhone" readonly/>
                        </FormGroup>
                        <FormGroup class="col-lg-2" :label="$t('labels.city')" required>
                            <AppAutoComplete
                                :url="`/projects/${$route.params.projectUuid}/crm/delivery/providers/${DeliveryIntegrationsEnum.ROZETKA}/cities`"
                                :model-value="senderCity?.uuid"
                                :selected-value="formatDeliveryCityName(senderCity)"
                                @update:model-value="(newCity) => {senderCity = newCity; senderWarehouse = null}"
                                :query-params="{
                                    orders: [
                                        { column: 'name', dir: 'asc'}
                                    ]
                                }"
                                :option-title-formatter="formatDeliveryCityName"
                            >
                                <template #optionItem="{option}">
                                    {{ formatDeliveryCityName(option) }}
                                </template>
                            </AppAutoComplete>
                        </FormGroup>
                        <FormGroup class="col-lg-3" :label="$t('labels.department')" required>
                            <AppAutoComplete
                                :url="`/projects/${$route.params.projectUuid}/crm/delivery/providers/${DeliveryIntegrationsEnum.ROZETKA}/cities/${senderCity?.uuid}/warehouses`"
                                :model-value="senderWarehouse?.uuid"
                                :selected-value="senderWarehouse?.name"
                                :disabled="!senderCity"
                                @update:model-value="senderWarehouse = $event"
                                :query-params="{
                                    orders: [
                                        { column: 'name', dir: 'asc'}
                                    ]
                                }"
                            />
                        </FormGroup>
                    </div>
                </div>
            </AppToast>
        </div>
        <div class="modal-footer d-flex justify-content-end">
            <AppButton color="light" class="mr-2" @click="hide()">
                {{ $t('buttons.dismiss') }}
            </AppButton>
            <AppButton color="indigo" :disabled="isCreating || !isFormFilled" @click="createWaybill()">
                <i :class="['mr-2', isCreating ? 'icon-spinner2 spinner' : 'icon-barcode2']"></i>
                {{ $t('buttons.create_waybill') }}
            </AppButton>
        </div>
    </AppModal>
</template>
<script>
import AppModal from "../../../../../../components/new/modal/AppModal.vue";
import AppButton from "../../../../../../components/new/button/AppButton.vue";
import FormGroup from "../../../../../../components/new/form/group/FormGroup.vue";
import Input from "../../../../../../components/new/form/Input.vue";
import PhoneInput from "../../../../../../components/new/form/PhoneInput.vue";
import AppAutoComplete from "../../../../../../components/new/form/autocomplete/AppAutoComplete.vue";
import AppToast from "../../../../../../components/new/AppToast.vue";
import DeliveryMethodSelect from "../../../../../components/select/DeliveryMethodSelect.vue";

import { formatDeliveryCityName } from "../../../../../../utils";
import { httpClient } from "../../../../../../services/HttpClient";
import MessageNotificator from "../../../../../../services/MessageNotificator";

import { DeliveryIntegrationsEnum } from "../../../../../enums/DeliveryIntegrationsEnum";

export default {
    name: "CreateRozetkaWaybillModal",
    components: {
        AppModal, AppButton, FormGroup, Input, PhoneInput, AppAutoComplete, AppToast, DeliveryMethodSelect
    },
    props: {
        recipientName: {
            type: String,
            default: ''
        },
        recipientPhone: {
            type: String,
            default: ''
        },
        orderSum: {
            type: Number,
            default: 0
        },
        orderUuid: {
            type: String,
            required: true
        },
        integrationUuid: {
            type: String,
            required: true
        },
        deliveryUuid: {
            type: String,
            required: true
        }
    },
    emits: ['created'],
    data() {
        return {
            DeliveryIntegrationsEnum,
            senderExpanded: false,
            senderType: null,
            senderName: '',
            senderPhone: '',
            senderCity: null,
            senderWarehouse: null,
            recipientCity: null,
            recipientWarehouse: null,
            codEnabled: true,
            codAmount: '',
            isInsuranceEnabled: true,
            seatsAmount: '',
            description: '',
            weight: '',
            length: '',
            width: '',
            height: '',
            isCreating: false
        };
    },
    computed: {
        senderTitle() {
            let title = `${this.$t('labels.sender')}<span class="badge badge-soft-success ml-2">${this.$t('labels.automatic')}</span>`;

            return title;
        },
        senderSummary() {
            let parts = [
                this.senderName,
                [this.senderCity?.name, this.senderWarehouse?.name].filter(Boolean).join(', '),
                this.formatPhone(this.senderPhone)
            ];

            let summary = parts.filter(Boolean).join(' · ');

            return summary;
        },
        isFormFilled() {
            let requiredValues = [
                this.senderType,
                this.senderName,
                this.senderPhone,
                this.senderCity,
                this.senderWarehouse
            ];

            if (this.codEnabled) {
                requiredValues.push(this.codAmount);
            }

            return requiredValues.every(value => value !== null && value !== '');
        }
    },
    methods: {
        formatDeliveryCityName,
        show() {
            this.codAmount = this.orderSum;
            this.description = '';
            this.recipientCity = null;
            this.recipientWarehouse = null;

            this.fetchCodEnabled();
            this.fetchCredential();
            this.fetchDelivery();

            this.$refs.modal.show();
        },
        hide() {
            this.$refs.modal.hide();
        },
        fetchDelivery() {
            httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/orders/${this.orderUuid}/deliveries/${this.deliveryUuid}`).then(response => {
                let delivery = response.data.data;

                this.recipientCity = delivery.city ?? null;
                this.recipientWarehouse = delivery.warehouse ?? null;
            });
        },
        fetchCodEnabled() {
            httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/orders/${this.orderUuid}/payments`).then(response => {
                let payment = response.data.data[0] ?? null;

                this.codEnabled = !(payment?.is_paid ?? false);
            });
        },
        /**
         * The api creates the waybill with the credentials of the order's integration, so the defaults are taken from them.
         */
        fetchCredential() {
            httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.integrationUuid}`).then(response => {
                this.applyDefaultFields(response.data.data.api_credentials?.settings?.default_fields ?? {});
            });
        },
        applyDefaultFields(defaultFields) {
            let dimensions = defaultFields.dimensions ?? {};

            this.senderType = defaultFields.sender_type ?? null;
            this.senderName = defaultFields.sender_name ?? '';
            this.senderPhone = defaultFields.sender_phone ?? '';

            this.senderCity = defaultFields.sender_city_uuid
                ? {
                    uuid: defaultFields.sender_city_uuid,
                    name: defaultFields.sender_city_name ?? '',
                    district: defaultFields.sender_city_district ?? null,
                    region: defaultFields.sender_city_region ?? null
                }
                : null;

            this.senderWarehouse = defaultFields.sender_address_uuid
                ? {uuid: defaultFields.sender_address_uuid, name: defaultFields.sender_address_name ?? ''}
                : null;

            this.senderExpanded = !this.senderCity || !this.senderWarehouse;

            this.seatsAmount = dimensions.seats_amount ?? 1;
            this.weight = dimensions.weight ?? '';
            this.length = dimensions.length ?? '';
            this.width = dimensions.width ?? '';
            this.height = dimensions.height ?? '';
        },
        formatPhone(phone) {
            let digits = String(phone ?? '').replace(/\D/g, '');

            if (digits.length !== 12) {
                return phone ?? '';
            }

            let formatted = `+${digits.slice(0, 2)} (${digits.slice(2, 5)}) ${digits.slice(5, 8)}-${digits.slice(8, 10)}-${digits.slice(10)}`;

            return formatted;
        },
        async createWaybill() {
            let dimensions = {
                seats_amount: this.toNumber(this.seatsAmount),
                weight: this.toNumber(this.weight),
                length: this.toNumber(this.length),
                width: this.toNumber(this.width),
                height: this.toNumber(this.height)
            };

            let request = {
                sender_city_uuid: this.senderCity?.uuid ?? null,
                sender_address_uuid: this.senderWarehouse?.uuid ?? null,
                dimensions: this.withoutEmptyValues(dimensions),
                has_paid: !this.codEnabled,
                is_insurance_enabled: this.isInsuranceEnabled,
                cost: this.codEnabled ? this.toNumber(this.codAmount) : null,
                description: this.description || null
            };

            this.isCreating = true;

            try {
                let response = await httpClient.post(
                    `/projects/${this.$route.params.projectUuid}/crm/orders/${this.orderUuid}/deliveries/${this.deliveryUuid}/create-ttn/rozetka/on-rozetka-marketplace`,
                    this.withoutEmptyValues(request),
                );

                this.$emit('created', response.data.data);

                new MessageNotificator().success(this.$t('alerts.success'));

                this.hide();
            } finally {
                this.isCreating = false;
            }
        },
        /**
         * The api validates every present field as a non-nullable value, so empty fields are left out of the request.
         */
        withoutEmptyValues(values) {
            let filtered = Object.fromEntries(
                Object.entries(values).filter(([, value]) => value !== null)
            );

            return filtered;
        },
        toNumber(value) {
            let number = value === '' || value === null || value === undefined ? null : Number(value);

            return number;
        }
    }
}
</script>
